import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LucideAngularModule, icons } from 'lucide-angular';
import { Subscription, take } from 'rxjs';
import { ApiFactoryEndPoints } from './common/api-factory-end-points';
import { AppConfigService } from './services/app-config.service';
import { ContactService } from './services/contact.service';
import { LocalStorageService } from './services/local-storage.service';
import { WebUserInfo } from './data/web-user-info';
import { LoaderComponent } from './common/loader/loader.component';
import { CookieConsentComponent } from './common/cookie-consent/cookie-consent.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LucideAngularModule, LoaderComponent, CookieConsentComponent],
  template: `
    <!-- Global loader visible across all routes -->
    <app-loader></app-loader>

        <!-- Global cookie consent banner -->
    <app-cookie-consent></app-cookie-consent>

    <!-- Page content routed here -->
    <router-outlet></router-outlet>
  `,
  styles: []
})
export class App {
  static lucideIcons = icons;
  title = 'Capital Food';
  currentPath: string = '';
  // showCta = true;

  route: string;
  locality: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  webUserInfo: WebUserInfo = new WebUserInfo();
  visitors: any;
  currentUrl: string;

  private saveWebUserInfoSubscription: Subscription;
  private getVisitorCountSubscription: Subscription;

  constructor(
    private router: Router,
    private localStorage: LocalStorageService,
    private activatedRoute: ActivatedRoute,
    private contactService: ContactService,
    private apiUrl: ApiFactoryEndPoints,
    private appConfig: AppConfigService,
    private dialog: MatDialog
  ) {
    // this.router.events.subscribe(() => {
    //   this.dialog.closeAll();   // 🔥 kills ghost overlays on every route change
    // });
    // this.router.events.subscribe(event => {
    //   if (event instanceof NavigationEnd) {
    //     this.showCta = !event.urlAfterRedirects.includes('/contact-us');
    //   }
    // });
  }

  ngOnInit() {
    this.currentUrl = window.location.host.replace(':', '');
    this.getVisitorCount();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.setGeoLocation.bind(this), this.handleLocationError.bind(this));
    }
  }

  setGeoLocation(position: { coords: { latitude: any; longitude: any } }) {
    const { coords: { latitude, longitude }, } = position;
    this.localStorage.setLocation(latitude, longitude);
    this.getGoogleMap(latitude, longitude);
  }

  // getGoogleMap(latitude: number, longitude: number) {
  //   this.mapsAPILoader.load().then(() => {
  //     this.getAddress(latitude, longitude);
  //   });
  // }
  getGoogleMap(latitude: number, longitude: number) {
    this.loadGoogleMapsAPI().then(() => {
      this.getAddress(latitude, longitude);
    }).catch((err) => {
    });
  }
  loadGoogleMapsAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if ((window as any).google && (window as any).google.maps) {
        resolve();
        return;
      }

      // Create a new <script> element
      const script = document.createElement('script');
      // 👇 Replace here with your actual API key
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.appConfig.setting.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = (error) => {
        console.error('❌ Error loading Google Maps:', error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }


  handleLocationError(error: GeolocationPositionError) {
    this.getClientInfo();
  }

  getAddress(latitude: number, longitude: number) {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const address = results[0].address_components;
        this.extractAddressComponents(address);
      } else {
        console.error("Geocoder failed due to:", status);
      }
    });
  }

  extractAddressComponents(address: google.maps.GeocoderAddressComponent[]) {
    for (const component of address) {
      switch (true) {
        case component.types.includes('route'):
          this.route = component.long_name;
          break;
        case component.types.includes('sublocality_level_1'):
          this.locality = component.long_name;
          break;
        case component.types.includes('locality'):
          this.city = component.long_name;
          break;
        case component.types.includes('administrative_area_level_1'):
          this.state = component.long_name;
          break;
        case component.types.includes('country'):
          this.country = component.long_name;
          break;
        case component.types.includes('postal_code'):
          this.postalCode = component.long_name;
          break;
        default:
          break;
      }
    }
    this.getClientInfo();
  }

  getClientIPAddress() {
    let clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    if (!clientIp) {
      var xhttp = new XMLHttpRequest();
      xhttp.onreadystatechange = () => {
        if (xhttp.readyState == 4 && xhttp.status == 200) {
          const ip = xhttp.responseText;
          this.localStorage.setItem("client_ip", JSON.stringify(ip));
        }
      };
      xhttp.open("GET", "https://api.ipify.org", true); // Ensure the URL starts with https://
      xhttp.send();
    }
  }

  transform(value: string) {
    var datePipe = new DatePipe("en-US");
    value = datePipe.transform(value, 'MM/dd/yyyy h:mm:ss');
    return value;
  }

  private getCookie(name: string) {
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  private setCookie(name: string, value: string, expireDays: number, path: string = '') {
    let d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    let expires: string = `expires=${d.toUTCString()}`;
    let cpath: string = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath}`;
  }

  getIntiatedFrom(): string {
    let referrer = document.referrer;

    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      if ('referrer' in params) {
        referrer = params['referrer'];
      }
    });

    return referrer;
  }

  detectBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  isMobileTabletOrPC(): string | undefined {
    const uA = window.navigator.userAgent || window.navigator.vendor;
    if ((/iPad|iPhone|iPod/.test(uA)) || (uA.includes('Mac') && 'ontouchend' in document)) {
      return 'iOS';
    }
    const osList = ['Windows', 'Android', 'Unix', 'Mac', 'Linux', 'BlackBerry'];
    for (const os of osList) {
      if (new RegExp(os, 'i').test(uA)) return os;
    }
    return undefined;
  }

  getBrowserVersion() {
    var browserVersion = '' + parseFloat(navigator.appVersion);
    var Offset, OffsetVersion, ix;
    var browserAgent = navigator.userAgent;

    if ((OffsetVersion = browserAgent.indexOf("Chrome")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 7);
    }
    // For Microsoft internet explorer 
    else if ((OffsetVersion = browserAgent.indexOf("MSIE")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 5);
    }
    // For Safari
    else if ((OffsetVersion = browserAgent.indexOf("Safari")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 7);
      if ((OffsetVersion = browserAgent.indexOf("Version")) != -1)
        browserVersion = browserAgent.substring(OffsetVersion + 8);
    }
    return browserVersion;
  }

  getJSessionId() {
    return /SESS\w*ID=([^;]+)/i.test(document.cookie) ? RegExp.$1 : "false";
  }


  getClientInfo() {
    this.getClientIPAddress();
    let currentDateTime = this.transform((new Date).toUTCString());
    if (!this.getCookie("inTime")) {
      this.setCookie("inTime", currentDateTime, 1);
    }
    this.webUserInfo.intiatedFrom = this.getIntiatedFrom();
    this.webUserInfo.clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    this.webUserInfo.cleintBrowser = this.detectBrowserName();
    this.webUserInfo.cleintDevice = this.isMobileTabletOrPC();
    this.webUserInfo.cleintBrowserVersion = this.getBrowserVersion();
    this.webUserInfo.sessionId = this.getJSessionId();
    this.webUserInfo.inTime = this.getCookie("inTime");
    this.webUserInfo.outTime = currentDateTime;
    this.webUserInfo.route = this.route;
    this.webUserInfo.locality = this.locality;
    this.webUserInfo.city = this.city;
    this.webUserInfo.state = this.state;
    this.webUserInfo.country = this.country;
    this.webUserInfo.postalCode = this.postalCode;
    this.webUserInfo.latitude = this.localStorage.getLocation().lat.toString();
    this.webUserInfo.longitude = this.localStorage.getLocation().lon.toString();
    this.webUserInfo.sourceUrl = this.currentUrl;

    this.saveWebUserInfo();
  }

  saveWebUserInfo() {
    if (this.webUserInfo.clientIp != '182.79.71.219' && this.webUserInfo.clientIp != '182.79.71.218') {
      this.saveWebUserInfoSubscription = this.contactService.saveWebUserInfo(this.apiUrl.insertWebUserInfo, this.webUserInfo).subscribe((data) => {
        if (data) {
          this.localStorage.setItem("web_user_info", JSON.stringify(this.webUserInfo));
        }
      });
    }
  }

  getVisitorCount() {
    let clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    const api = clientIp != '182.79.71.219' && '182.79.71.218'
      ? this.apiUrl.getVisitorCount   // increments count
      : this.apiUrl.getCount;         // just fetch count
    this.getVisitorCountSubscription = this.contactService.getVisitorCount(api, "home").subscribe((data: any) => {
      this.visitors = data;
      this.localStorage.setItem('visitors', JSON.stringify(this.visitors));
    });
  }

}
